import React, { useState } from 'react';

import { DEFAULT_INSIGHT_PIN_LAYOUT } from '@aline/common';
import { link } from '@aline/routes';
import { useApolloClient } from '@apollo/client/react';
import { Alert, Button, Chip, IconButton, Menu, MenuItem, Skeleton } from '@mui/material';
import type { IconButtonProps } from '@mui/material';
import { ArrowLeft, Bookmark, ChevronDown, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { EditableNameInput } from 'src/components';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { useCurrentUserQuery } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { doNothing } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { useCreateDraftViewAndOpenBuilder } from './create-draft-view-and-open-builder';
import {
  getCanEditInsightView,
  getChartPinScope,
  getChartsWithNewChart,
  getChartsWithoutChart,
  getMutationFailureMessage,
  getStateWithCharts,
  INSIGHT_VIEW_EDIT_DISABLED_REASON,
} from './insight-chart-builder-state';
import { useInsightBuilderDraft } from './use-insight-builder-draft';
import {
  createDefaultWorkflowListChartConfig,
  getNextWorkflowListChartId,
  UNTITLED_WORKFLOW_LIST_CHART_TITLE,
} from '../workflow/workflow-list/workflow-list-charts';
import { DEFAULT_WORKFLOW_LIST_PRESET } from '../workflow/workflow-list/workflow-views';
import { WorkflowsList } from '../workflow/workflow-list/WorkflowsList';

const DELETE_CHART_COPY = {
  message: 'Delete this chart from the view and remove its insights placement?',
  primaryText: 'Delete chart',
  title: 'Delete chart?',
};

const useStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  editor: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  readOnlyAlert: {
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  scopeChip: {
    '& .MuiChip-deleteIcon': {
      '&:hover': {
        color: 'inherit',
      },
      color: 'inherit',
    },
  },
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    minHeight: 56,
    minWidth: 0,
    paddingBlock: theme.spacing(0.75),
  },
  toolbarName: {
    maxWidth: 320,
    minWidth: 120,
  },
}));

function BuilderIconButton({
  children,
  disabled,
  disabledReason,
  onClick,
  placement = 'bottom',
  title,
  ...buttonProps
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  disabledReason?: string;
  placement?: 'bottom' | 'top';
} & Pick<IconButtonProps, 'aria-label' | 'aria-pressed' | 'color'>) {
  return (
    <AlineTooltip arrow placement={placement} title={disabledReason ?? title}>
      <span>
        <IconButton disabled={disabled} size="small" onClick={onClick} {...buttonProps}>
          {children}
        </IconButton>
      </span>
    </AlineTooltip>
  );
}

function ChartScopeChip({
  chartId,
  classes,
  disabled,
  disabledReason,
  onOpen,
  scope,
}: {
  chartId: string;
  classes: { scopeChip: string };
  disabled: boolean;
  onOpen: (menu: { anchorEl: HTMLElement; chartId: string }) => void;
  scope: Apollo.InsightScope;
  disabledReason?: string;
}) {
  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    onOpen({ anchorEl: event.currentTarget, chartId });
  };
  const chip = (
    <Chip
      className={classes.scopeChip}
      deleteIcon={<ChevronDown size={14} />}
      disabled={disabled}
      label={scope === Apollo.InsightScope.Org ? 'Org' : 'Personal'}
      size="small"
      variant="outlined"
      onClick={openMenu}
      onDelete={openMenu}
    />
  );
  if (!disabledReason) return chip;

  return (
    <AlineTooltip arrow placement="top" title={disabledReason}>
      <span>{chip}</span>
    </AlineTooltip>
  );
}

export function InsightChartBuilderPage() {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { viewId } = useParams<{ viewId: string }>();
  const [chartFieldsRequestKey, setChartFieldsRequestKey] = useState(0);
  const { currentUser, loading: currentUserLoading } = useCurrentUserQuery();
  const [chartIdToDelete, setChartIdToDelete] = useState<string | null>(null);
  const [chartTitleEditingKey, setChartTitleEditingKey] = useState<string | null>(null);
  const [chartOperationPending, setChartOperationPending] = useState(false);
  const [deleteViewDialogOpen, setDeleteViewDialogOpen] = useState(false);
  const [scopeMenu, setScopeMenu] = useState<{
    anchorEl: HTMLElement;
    chartId: string;
  } | null>(null);

  const { data } = Apollo.useGetWorkflowViewQuery({
    skip: !viewId,
    variables: { id: viewId ?? '' },
  });
  const [updateSavedViewsVisibility, { loading: togglingSavedViews }] =
    Apollo.useUpdateWorkflowViewMutation();
  const [createInsightPin, { loading: pinning }] = Apollo.useCreateInsightPinMutation();
  const [updateInsightPin, { loading: movingPin }] = Apollo.useUpdateInsightPinMutation();
  const [addWorkflowViewChart, { loading: addingChart }] = Apollo.useAddWorkflowViewChartMutation();
  const [deleteWorkflowViewChart, { loading: deletingChart }] =
    Apollo.useDeleteWorkflowViewChartMutation();
  const { createDraftViewAndOpenBuilder, loading: duplicating } =
    useCreateDraftViewAndOpenBuilder();
  const [deleteWorkflowView, { loading: deletingView }] = Apollo.useDeleteWorkflowViewMutation();

  const view = data?.workflowView;
  const isOrgAdmin = currentUser?.role === Apollo.OrgRole.Admin;
  const canEdit = getCanEditInsightView({
    authorId: view?.author.id,
    isOrgAdmin,
    userId: currentUser?.id,
  });
  const editDisabledReason = !canEdit ? INSIGHT_VIEW_EDIT_DISABLED_REASON : undefined;
  const draft = useInsightBuilderDraft({
    canEdit,
    paused: chartOperationPending,
    view,
    viewId,
  });

  const notifyFailure = (error: unknown, fallback: string) => {
    enqueueSnackbar(getMutationFailureMessage(error, fallback), { variant: 'error' });
  };

  const evictInsightsDashboard = () => {
    apolloClient.cache.evict({ fieldName: 'workflowInsightsDashboard', id: 'ROOT_QUERY' });
    apolloClient.cache.gc();
  };

  const runExclusive = async (work: () => Promise<void>) => {
    try {
      setChartOperationPending(true);
      await work();
    } finally {
      setChartOperationPending(false);
    }
  };

  const handleBack = async () => {
    await draft.flush();
    evictInsightsDashboard();
    navigate(link.WorkflowInsights());
  };

  const handleAddChart = async () => {
    if (!canEdit || !view || !draft.currentState || addingChart || chartOperationPending) return;

    const chartId = getNextWorkflowListChartId(
      Object.keys(draft.chartsIntegrationSlice.charts ?? {})
    );
    const nextState = getStateWithCharts(
      draft.currentState,
      getChartsWithNewChart(
        draft.chartsIntegrationSlice,
        chartId,
        createDefaultWorkflowListChartConfig({ title: UNTITLED_WORKFLOW_LIST_CHART_TITLE })
      )
    );

    await runExclusive(async () => {
      try {
        await draft.cancel();
        const result = await addWorkflowViewChart({
          variables: {
            input: {
              pin: {
                chartId,
                layout: DEFAULT_INSIGHT_PIN_LAYOUT,
                scope: Apollo.InsightScope.Personal,
              },
              state: nextState,
              viewId: view.id,
            },
          },
        });
        if (!result.data?.addWorkflowViewChart.workflowView) {
          enqueueSnackbar(result.error?.message || 'Failed to add chart.', { variant: 'error' });
          return;
        }
        draft.rememberPersistedState(nextState);
        setChartTitleEditingKey(chartId);
        setChartFieldsRequestKey(previous => previous + 1);
      } catch (error) {
        notifyFailure(error, 'Failed to add chart.');
      }
    });
  };

  const handleMoveChart = async (chartId: string, scope: Apollo.InsightScope) => {
    if (!view || movingPin || pinning) return;

    setScopeMenu(null);
    const pin = view.pins.find(candidate => candidate.chartId === chartId);
    try {
      if (pin) {
        const result = await updateInsightPin({
          variables: { input: { id: pin.id, props: { scope } } },
        });
        if (!result.data?.updateInsightPin.insightPin)
          enqueueSnackbar(result.error?.message || 'Failed to move chart.', { variant: 'error' });

        return;
      }

      const result = await createInsightPin({
        update(cache, { data: result }) {
          const insightPin = result?.createInsightPin.insightPin;
          if (!insightPin) return;

          cache.updateQuery<Apollo.GetWorkflowViewQuery, Apollo.GetWorkflowViewQueryVariables>(
            { query: Apollo.GetWorkflowViewDocument, variables: { id: view.id } },
            existing => {
              if (!existing?.workflowView) return existing;
              if (existing.workflowView.pins.some(candidate => candidate.id === insightPin.id)) {
                return existing;
              }
              return {
                ...existing,
                workflowView: {
                  ...existing.workflowView,
                  pins: [...existing.workflowView.pins, insightPin],
                },
              };
            }
          );
        },
        variables: {
          input: {
            chartId,
            layout: DEFAULT_INSIGHT_PIN_LAYOUT,
            scope,
            viewId: view.id,
          },
        },
      });
      if (!result.data?.createInsightPin.insightPin)
        enqueueSnackbar(result.error?.message || 'Failed to move chart.', { variant: 'error' });
    } catch (error) {
      notifyFailure(error, 'Failed to move chart.');
    }
  };

  const handleToggleShowInSavedViews = async () => {
    if (!view || !canEdit || togglingSavedViews) return;

    try {
      const result = await updateSavedViewsVisibility({
        variables: {
          input: {
            id: view.id,
            props: { showInSavedViews: !view.showInSavedViews },
          },
        },
      });
      if (!result.data?.updateWorkflowView.workflowView)
        enqueueSnackbar(result.error?.message || 'Failed to update saved views visibility.', {
          variant: 'error',
        });
    } catch (error) {
      notifyFailure(error, 'Failed to update saved views visibility.');
    }
  };

  const handleDeleteView = async () => {
    if (!view || !canEdit || chartOperationPending) return;

    try {
      await draft.cancel();
      const result = await deleteWorkflowView({ variables: { id: view.id } });
      if (!result.data?.deleteWorkflowView.id) {
        enqueueSnackbar(result.error?.message || 'Failed to delete view.', { variant: 'error' });
        return;
      }
      apolloClient.cache.evict({ id: apolloClient.cache.identify(view) });
      evictInsightsDashboard();
      navigate(link.WorkflowInsights());
    } catch (error) {
      notifyFailure(error, 'Failed to delete view.');
    }
  };

  const handleDuplicateView = async () => {
    if (!view || !draft.currentState || chartOperationPending) return;

    await draft.flush();
    await createDraftViewAndOpenBuilder({
      chartId: draft.activeChartId,
      failureMessage: 'Failed to duplicate view.',
      name: `${draft.name} copy`,
      state: draft.currentState,
    });
  };

  const handleDeleteChart = async (chartId: string) => {
    if (!view || !draft.currentState || deletingChart || chartOperationPending) return;

    const nextCharts = getChartsWithoutChart(draft.chartsIntegrationSlice, chartId);
    const nextActiveChartId = nextCharts.activeChartId;
    if (!nextActiveChartId) return;

    const nextState = getStateWithCharts(draft.currentState, nextCharts);

    await runExclusive(async () => {
      try {
        await draft.cancel();
        const result = await deleteWorkflowViewChart({
          variables: { input: { chartId, state: nextState, viewId: view.id } },
        });
        if (!result.data?.deleteWorkflowViewChart.workflowView) {
          enqueueSnackbar(result.error?.message || 'Failed to delete chart.', { variant: 'error' });
          return;
        }
        draft.rememberPersistedState(nextState);
        draft.setActiveChartId(nextActiveChartId);
      } catch (error) {
        notifyFailure(error, 'Failed to delete chart.');
      }
    });
  };

  /**
   * Only the first load blanks the editor. Cache updates keep the same editor
   * session alive rather than reapplying persisted state after every mutation.
   * The viewer has to be known too: until then `canEdit` is false, which would
   * flash the read-only banner and a locked grid at an author who can edit.
   */
  if (!view || !draft.editorSession || view.id !== viewId || currentUserLoading) {
    return (
      <div className={classes.loading}>
        <Skeleton height={44} variant="rounded" />
        <Skeleton height={540} variant="rounded" />
      </div>
    );
  }

  const chartIds = Object.keys(draft.chartsIntegrationSlice.charts ?? {});
  const savedViewsLabel = view.showInSavedViews ? 'Remove from saved views' : 'Show in saved views';
  const scopeMenuChartScope = scopeMenu
    ? getChartPinScope(view.pins, scopeMenu.chartId)
    : undefined;

  return (
    <div className={classes.editor}>
      <div className={classes.toolbar}>
        <AlineTooltip arrow placement="bottom" title="Back to insights">
          <IconButton aria-label="Back to insights" onClick={() => void handleBack()}>
            <ArrowLeft size={18} />
          </IconButton>
        </AlineTooltip>
        <AlineTooltip arrow placement="bottom" title={editDisabledReason ?? ''}>
          <span className={classes.toolbarName}>
            <EditableNameInput
              disabled={!canEdit || chartOperationPending}
              name={draft.name}
              placeholder="Name this view..."
              updateName={draft.setName}
              wrapperClassName={classes.toolbarName}
            />
          </span>
        </AlineTooltip>
        <div className={classes.actions}>
          <BuilderIconButton
            aria-label={savedViewsLabel}
            aria-pressed={view.showInSavedViews}
            color={view.showInSavedViews ? 'primary' : 'default'}
            disabled={!canEdit || togglingSavedViews}
            disabledReason={editDisabledReason}
            title={savedViewsLabel}
            onClick={() => void handleToggleShowInSavedViews()}
          >
            <Bookmark fill={view.showInSavedViews ? 'currentColor' : 'none'} size={18} />
          </BuilderIconButton>
          <BuilderIconButton
            aria-label="Delete view"
            disabled={!canEdit}
            disabledReason={editDisabledReason}
            title="Delete view"
            onClick={() => setDeleteViewDialogOpen(true)}
          >
            <Trash2 size={18} />
          </BuilderIconButton>
        </div>
      </div>
      {!canEdit && (
        <Alert
          action={
            <Button
              disabled={duplicating}
              variant="outlined"
              onClick={() => void handleDuplicateView()}
            >
              Duplicate and edit
            </Button>
          }
          className={classes.readOnlyAlert}
          severity="info"
        >
          This View is managed by your organization. Duplicate it to make your own editable copy.
        </Alert>
      )}
      <div className={classes.body}>
        <WorkflowsList
          chartFieldsRequestKey={chartFieldsRequestKey}
          chartTitleEditingKey={chartTitleEditingKey}
          chartsIntegrationSlice={draft.chartsIntegrationSlice}
          filtersAnchorEl={null}
          initialViewState={draft.editorSession.initialState}
          readOnly={!canEdit || chartOperationPending}
          renderChartActions={
            chartIds.length > 1
              ? chartId => (
                  <BuilderIconButton
                    aria-label="Delete chart"
                    disabled={!canEdit || chartOperationPending}
                    disabledReason={editDisabledReason}
                    placement="top"
                    title="Delete chart"
                    onClick={() => setChartIdToDelete(chartId)}
                  >
                    <Trash2 size={16} />
                  </BuilderIconButton>
                )
              : undefined
          }
          renderChartLeadingActions={chartId => (
            <ChartScopeChip
              chartId={chartId}
              classes={classes}
              disabled={!canEdit || movingPin || pinning || chartOperationPending}
              disabledReason={editDisabledReason}
              scope={getChartPinScope(view.pins, chartId)}
              onOpen={setScopeMenu}
            />
          )}
          variant="chart-builder"
          workflowListPreset={DEFAULT_WORKFLOW_LIST_PRESET}
          onActiveChartTitleChange={draft.handleChartTitleChange}
          onAddChart={handleAddChart}
          onApiRefChange={doNothing}
          onChartsIntegrationSliceChange={draft.handleChartSliceChange}
          onViewStateChange={canEdit ? draft.handleViewStateChange : undefined}
        />
      </div>
      <Menu
        anchorEl={scopeMenu?.anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(scopeMenu)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setScopeMenu(null)}
      >
        <MenuItem
          selected={scopeMenuChartScope === Apollo.InsightScope.Personal}
          onClick={() => {
            if (scopeMenu) void handleMoveChart(scopeMenu.chartId, Apollo.InsightScope.Personal);
          }}
        >
          Personal
        </MenuItem>
        <AlineTooltip
          arrow
          placement="right"
          title={!isOrgAdmin ? 'Only admins can publish an org chart' : ''}
        >
          <span>
            <MenuItem
              disabled={!isOrgAdmin}
              selected={scopeMenuChartScope === Apollo.InsightScope.Org}
              onClick={() => {
                if (scopeMenu) void handleMoveChart(scopeMenu.chartId, Apollo.InsightScope.Org);
              }}
            >
              Org
            </MenuItem>
          </span>
        </AlineTooltip>
      </Menu>
      <ConfirmationDialog
        message={DELETE_CHART_COPY.message}
        open={Boolean(chartIdToDelete)}
        primaryDisabledReason={deletingChart || chartOperationPending ? 'Updating Insights…' : ''}
        primaryText={DELETE_CHART_COPY.primaryText}
        title={DELETE_CHART_COPY.title}
        onClickPrimary={() => {
          const chartId = chartIdToDelete;
          setChartIdToDelete(null);
          if (!chartId) return;
          void handleDeleteChart(chartId);
        }}
        onClose={() => setChartIdToDelete(null)}
      />
      <ConfirmationDialog
        message={`Delete the “${view.name}” view and its Insights placements? This cannot be undone.`}
        open={deleteViewDialogOpen}
        primaryDisabledReason={deletingView ? 'Deleting view…' : ''}
        primaryText="Delete view"
        title="Delete view?"
        onClickPrimary={() => void handleDeleteView()}
        onClose={() => setDeleteViewDialogOpen(false)}
      />
    </div>
  );
}
